// Stock projection waterfall v2: the drawing walk (handover 2026-09-24 §2.1).
//
// Pure functions over the PlanningBookLedger rows the API returns
// (…/stock-projections-exceptions-waterfall): no React, no Plotly. The later
// MUI X port reuses this module as-is and changes only the renderer.
//
// Each week starts at opening stock (A1); the four steps run in order, and
// inside a step each series moves the running level by its signed value, so
// every segment is an explicit {low, high} range. The last step ends on A3 up
// to the ledger's residual D1, which is not in the rows (≤ 7e-5 on TST): the
// gap is reported, never asserted.

export type LedgerCode =
  | 'C1'
  | 'C2'
  | 'B1'
  | 'B2.1'
  | 'B2.2'
  | 'B3.1'
  | 'B3.2'
  | 'B3.3'
  | 'B4'
  | 'C3.1'
  | 'C3.2'
  | 'C3.3'
  | 'C4'

// One row per (base, replenishment site, week), the selected SKUs summed
// (W1 contract). C4.1 / C4.2 are carried for Classic's recomposition only;
// E5 (target closing inventory) for the hover only. productionLeadWeeks is
// the selected SKUs' deployment lead time in whole weeks, null when they
// disagree (US154348): the hover's week of production.
export type WaterfallRow = {
  baseSourceSiteKey: number
  replenishmentSite: string
  dateKey: number
  YearISOWeek: string
  demandMode: string | null
  A1: number
  A3: number
  E1: number
  E5: number
  productionLeadWeeks?: number | null
} & Record<LedgerCode, number>

export type StepId = 'demand' | 'productionIn' | 'movementsIn' | 'movementsOut'
export type Family = 'amber' | 'brown' | 'red' | 'teal' | 'blue' | 'slate'
// Fill carries firmness: solid = scheduled / committed, light = generated by
// the frozen-period pull, hatched = planned by DRP, crossHatched = a cut.
export type Fill = 'solid' | 'light' | 'hatched' | 'crossHatched'

export type StepDef = {
  id: StepId
  label: string
  short: string
  initial: string
}

// `short` is the step-relative name the week hover shows inside its step's
// line (US154407); the legend and everything else use `label`.
export type SeriesDef = {
  id: string
  label: string
  short: string
  step: StepId
  code: LedgerCode
  sign: 1 | -1
  family: Family
  fill: Fill
}

export const STEPS: StepDef[] = [
  { id: 'demand', label: 'Demand', short: 'Demand', initial: 'D' },
  { id: 'productionIn', label: 'Production in', short: 'Prod', initial: 'P' },
  { id: 'movementsIn', label: 'Movements in', short: 'In', initial: 'I' },
  { id: 'movementsOut', label: 'Movements out', short: 'Out', initial: 'O' },
]

// Draw order (§2.2). B3.2 and C3.2 are published negative, so a cut moves the
// level back from its scheduled segment.
export const SERIES: SeriesDef[] = [
  {
    id: 'customerDemand',
    label: 'Effective customer demand',
    short: 'Customer',
    step: 'demand',
    code: 'C1',
    sign: -1,
    family: 'amber',
    fill: 'solid',
  },
  {
    id: 'componentDemand',
    label: 'Component demand',
    short: 'Component',
    step: 'demand',
    code: 'C2',
    sign: -1,
    family: 'brown',
    fill: 'solid',
  },
  {
    id: 'stockInTransit',
    label: 'Stock in transit arrival',
    short: 'In transit',
    step: 'productionIn',
    code: 'B1',
    sign: 1,
    family: 'red',
    fill: 'solid',
  },
  {
    id: 'scheduledProduction',
    label: 'Scheduled production arrival',
    short: 'Scheduled',
    step: 'productionIn',
    code: 'B2.1',
    sign: 1,
    family: 'teal',
    fill: 'solid',
  },
  {
    id: 'plannedProduction',
    label: 'Planned production arrival',
    short: 'Planned',
    step: 'productionIn',
    code: 'B2.2',
    sign: 1,
    family: 'teal',
    fill: 'hatched',
  },
  {
    id: 'scheduledArrival',
    label: 'Scheduled movement arrival (loaded, as committed)',
    short: 'Scheduled',
    step: 'movementsIn',
    code: 'B3.1',
    sign: 1,
    family: 'blue',
    fill: 'solid',
  },
  {
    id: 'arrivalCut',
    label: 'Cut on scheduled arrival',
    short: 'Cut',
    step: 'movementsIn',
    code: 'B3.2',
    sign: 1,
    family: 'blue',
    fill: 'crossHatched',
  },
  {
    id: 'generatedArrival',
    label: 'Generated movement arrival',
    short: 'Generated',
    step: 'movementsIn',
    code: 'B3.3',
    sign: 1,
    family: 'blue',
    fill: 'light',
  },
  {
    id: 'plannedArrival',
    label: 'Planned movement arrival',
    short: 'Planned',
    step: 'movementsIn',
    code: 'B4',
    sign: 1,
    family: 'blue',
    fill: 'hatched',
  },
  {
    id: 'scheduledDispatch',
    label: 'Scheduled movement dispatch (loaded, as committed)',
    short: 'Scheduled',
    step: 'movementsOut',
    code: 'C3.1',
    sign: -1,
    family: 'slate',
    fill: 'solid',
  },
  {
    id: 'dispatchCut',
    label: 'Cut on scheduled dispatch',
    short: 'Cut',
    step: 'movementsOut',
    code: 'C3.2',
    sign: -1,
    family: 'slate',
    fill: 'crossHatched',
  },
  {
    id: 'generatedDispatch',
    label: 'Generated movement dispatch',
    short: 'Generated',
    step: 'movementsOut',
    code: 'C3.3',
    sign: -1,
    family: 'slate',
    fill: 'light',
  },
  {
    id: 'plannedDispatch',
    label: 'Planned movement dispatch',
    short: 'Planned',
    step: 'movementsOut',
    code: 'C4',
    sign: -1,
    family: 'slate',
    fill: 'hatched',
  },
]

export type Segment = {
  series: SeriesDef
  value: number
  low: number
  high: number
  levelAfter: number
}

export type StepPoint = {
  step: StepDef
  start: number
  end: number
  segments: Segment[]
}

export type WeekWalk = {
  dateKey: number
  yearIsoWeek: string
  label: string
  opening: number
  closing: number
  safetyStock: number
  targetClosing: number
  productionLeadWeeks: number | null
  steps: StepPoint[]
  closingGap: number
}

export type SiteWalk = {
  site: string
  weeks: WeekWalk[]
}

export type RowSelection = {
  sites?: string[] | null
  startDateKey?: number | null
  endDateKey?: number | null
}

const SUMMED: (keyof WaterfallRow)[] = [
  'A1',
  'A3',
  'E1',
  'E5',
  ...SERIES.map((s) => s.code),
]

const num = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : 0)

// The page's site filter and week range, as Classic applies them.
export const selectRows = (
  rows: WaterfallRow[],
  { sites, startDateKey, endDateKey }: RowSelection
): WaterfallRow[] => {
  const siteSet = sites ? new Set(sites) : null
  const start = startDateKey ?? 0
  const end = endDateKey ?? Number.MAX_SAFE_INTEGER
  return rows.filter(
    (r) =>
      (!siteSet || siteSet.has(r.replenishmentSite)) &&
      r.dateKey >= start &&
      r.dateKey <= end
  )
}

// Week labels for the x axis: W39, or W39 '27 when the window repeats a week
// number (a horizon past 52 weeks), so categories never merge.
export const weekLabels = (yearIsoWeeks: string[]): Map<string, string> => {
  const unique = [...new Set(yearIsoWeeks)].sort()
  const weekNumbers = unique.map((y) => y.slice(4))
  const repeats = new Set(weekNumbers).size < weekNumbers.length
  return new Map(
    unique.map((y) => [
      y,
      repeats ? `W${y.slice(4)} '${y.slice(2, 4)}` : `W${y.slice(4)}`,
    ])
  )
}

// Sums rows per (site, week): a site reached from several bases (possible when
// several work centres are read) is one multiple, as in Classic. The lead time
// is not summed: it survives the merge only when every base agrees.
const bySiteWeek = (rows: WaterfallRow[]) => {
  const sites = new Map<string, Map<number, WaterfallRow>>()
  rows.forEach((row) => {
    const site = String(row.replenishmentSite)
    if (!sites.has(site)) sites.set(site, new Map())
    const weeks = sites.get(site) as Map<number, WaterfallRow>
    const acc = weeks.get(row.dateKey)
    if (!acc) {
      const copy = {
        ...row,
        replenishmentSite: site,
        productionLeadWeeks: row.productionLeadWeeks ?? null,
      }
      SUMMED.forEach(
        (k) => ((copy as Record<string, unknown>)[k] = num(row[k]))
      )
      weeks.set(row.dateKey, copy)
    } else {
      SUMMED.forEach(
        (k) => ((acc as Record<string, unknown>)[k] = num(acc[k]) + num(row[k]))
      )
      if (acc.productionLeadWeeks !== (row.productionLeadWeeks ?? null)) {
        acc.productionLeadWeeks = null
      }
    }
  })
  return sites
}

export const walkWeek = (row: WaterfallRow, label: string): WeekWalk => {
  let level = num(row.A1)
  const steps = STEPS.map((step) => {
    const start = level
    const segments = SERIES.filter((s) => s.step === step.id).map((series) => {
      const value = series.sign * num(row[series.code])
      const low = Math.min(level, level + value)
      const high = Math.max(level, level + value)
      level += value
      return { series, value, low, high, levelAfter: level }
    })
    return { step, start, end: level, segments }
  })
  return {
    dateKey: row.dateKey,
    yearIsoWeek: String(row.YearISOWeek),
    label,
    opening: num(row.A1),
    closing: num(row.A3),
    safetyStock: num(row.E1),
    targetClosing: num(row.E5),
    productionLeadWeeks: row.productionLeadWeeks ?? null,
    steps,
    closingGap: level - num(row.A3),
  }
}

// walk(rows) → one SiteWalk per replenishment site (sites sorted, weeks by
// dateKey), every week's four steps and their segments.
export const walk = (rows: WaterfallRow[]): SiteWalk[] => {
  const labels = weekLabels(rows.map((r) => String(r.YearISOWeek)))
  const sites = bySiteWeek(rows)
  return [...sites.keys()].sort().map((site) => {
    const weeks = [...(sites.get(site) as Map<number, WaterfallRow>).values()]
      .sort((a, b) => a.dateKey - b.dateKey)
      .map((row) =>
        walkWeek(row, labels.get(String(row.YearISOWeek)) ?? row.YearISOWeek)
      )
    return { site, weeks }
  })
}

// One y-range shared by every multiple: every level, segment, opening and
// safety stock, from zero unless something is negative, padded 6 %.
export const sharedRange = (sites: SiteWalk[]): [number, number] => {
  let lo = 0
  let hi = 0
  sites.forEach(({ weeks }) =>
    weeks.forEach((week) => {
      lo = Math.min(lo, week.opening, week.safetyStock)
      hi = Math.max(hi, week.opening, week.safetyStock)
      week.steps.forEach((step) => {
        lo = Math.min(lo, step.start, step.end)
        hi = Math.max(hi, step.start, step.end)
        step.segments.forEach((s) => {
          lo = Math.min(lo, s.low)
          hi = Math.max(hi, s.high)
        })
      })
    })
  )
  const pad = (hi - lo) * 0.06 || 1
  return [lo < 0 ? lo - pad : 0, hi + pad]
}

// Series that carry a value anywhere in the walk (the legend dims the rest).
export const liveSeries = (sites: SiteWalk[], epsilon = 1e-6): Set<string> => {
  const live = new Set<string>()
  sites.forEach(({ weeks }) =>
    weeks.forEach((week) =>
      week.steps.forEach((step) =>
        step.segments.forEach((s) => {
          if (Math.abs(s.value) > epsilon) live.add(s.series.id)
        })
      )
    )
  )
  return live
}
